import * as React from "react"
import { cn } from "@/lib/utils"

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-xl border border-kinky-600/20 bg-dark-950 text-white shadow-lg shadow-kinky-500/10 backdrop-blur-sm transition-all duration-500 hover:shadow-kinky-400/15 hover:border-kinky-500/30",
      className
    )}
    {...props}
  />
))
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6 border-b border-kinky-600/10", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn("font-kinky font-semibold leading-none tracking-tight text-white text-shadow", className)}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-secondary-300 leading-relaxed", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

// Kinky-themed card variants
const CardKinky = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-xl border border-kinky-600/30 bg-gradient-to-br from-dark-950 via-dark-900 to-kinky-950/20 text-white shadow-xl shadow-kinky-500/15 backdrop-blur-sm transition-all duration-500 hover:shadow-kinky-400/25 hover:border-kinky-500/60 hover:animate-kinky-glow",
      className
    )}
    {...props}
  />
))
CardKinky.displayName = "CardKinky"

const CardSensual = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-xl border border-passion-600/20 bg-gradient-to-br from-dark-900 via-passion-950/10 to-dark-900 text-white shadow-lg shadow-passion-500/10 backdrop-blur-md transition-all duration-400 hover:shadow-passion-400/20 hover:animate-sensual-pulse",
      className
    )}
    {...props}
  />
))
CardSensual.displayName = "CardSensual"

const CardArtistic = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
              "rounded-xl border border-artistic-600/25 bg-gradient-to-br from-dark-950 via-artistic-950/20 to-dark-900 text-white shadow-xl backdrop-blur-sm transition-all duration-600 hover:animate-artistic-bloom",
      className
    )}
    {...props}
  />
))
CardArtistic.displayName = "CardArtistic"

const CardLuxury = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-xl border border-luxury-600/30 bg-gradient-to-br from-dark-900 via-dark-800 to-luxury-950/20 text-white shadow-xl shadow-luxury-500/10 backdrop-blur-sm transition-all duration-500 hover:shadow-luxury-400/20 hover:animate-luxury-shimmer",
      className
    )}
    {...props}
  />
))
CardLuxury.displayName = "CardLuxury"

export { 
  Card, 
  CardHeader, 
  CardFooter, 
  CardTitle, 
  CardDescription, 
  CardContent,
  CardKinky,
  CardSensual,
  CardArtistic, 
  CardLuxury
}